import { useEffect } from "react";
import { X } from "lucide-react";
import { cn } from "@ridereserv/shared/utils";
import { useToastStore, type ToastItem } from "@/stores/toastStore";

/** Auto-dismiss delays — errors linger longer so they can be read. */
const DISMISS_MS = { success: 3000, error: 6000 } as const;

/**
 * A single toast row. Carries its own live-region semantics (`role="status"` +
 * `aria-live="polite"` for success, `role="alert"` + `aria-live="assertive"`
 * for errors) so assistive tech announces it on insert. Auto-dismisses after a
 * kind-dependent delay; also manually dismissible.
 * @param root0 - Props
 * @param root0.toast - The toast to render
 * @returns The toast element
 */
function ToastRow({ toast }: { toast: ToastItem }): React.JSX.Element {
  const removeToast = useToastStore((s) => s.removeToast);
  const isError = toast.kind === "error";

  useEffect(() => {
    const timer = setTimeout(
      () => {
        removeToast(toast.id);
      },
      isError ? DISMISS_MS.error : DISMISS_MS.success
    );
    return () => {
      clearTimeout(timer);
    };
  }, [toast.id, isError, removeToast]);

  return (
    <div
      role={isError ? "alert" : "status"}
      aria-live={isError ? "assertive" : "polite"}
      className={cn(
        "pointer-events-auto flex w-full items-start gap-2 rounded-lg border p-3 text-sm font-medium shadow-lg",
        isError
          ? "border-destructive-300 bg-destructive-50 text-destructive-800"
          : "border-success-300 bg-success-50 text-success-800"
      )}
    >
      <span className="flex-1 pt-0.5">{toast.message}</span>
      <button
        type="button"
        aria-label="Dismiss notification"
        onClick={() => {
          removeToast(toast.id);
        }}
        className={cn(
          "-m-1 inline-flex min-h-11 min-w-11 shrink-0 items-center justify-center rounded-sm",
          "pointer-fine:min-h-8 pointer-fine:min-w-8",
          "focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-1",
          isError
            ? "text-destructive-700 hover:text-destructive-900 focus-visible:ring-destructive-500 focus-visible:ring-offset-destructive-50"
            : "text-success-700 hover:text-success-900 focus-visible:ring-success-500 focus-visible:ring-offset-success-50"
        )}
      >
        <X className="h-3.5 w-3.5" strokeWidth={1.75} aria-hidden="true" />
      </button>
    </div>
  );
}

/**
 * App-level toast outlet. Mount once near the app root; it subscribes to the
 * {@link useToastStore} and renders the active toasts in a fixed,
 * bottom-anchored stack (full-width on mobile, right-aligned card on ≥sm).
 *
 * Raise toasts from anywhere with the `toast` helper from `@/stores/toastStore`
 * (e.g. a mutation `onSuccess`/`onError`). The container itself is
 * pointer-transparent so it never blocks clicks; only the toast cards capture
 * pointer events.
 * @returns The toast outlet (renders nothing when empty)
 */
export function Toaster(): React.JSX.Element | null {
  const toasts = useToastStore((s) => s.toasts);
  if (toasts.length === 0) return null;

  return (
    <div className="pointer-events-none fixed inset-x-4 bottom-4 z-[60] flex flex-col items-stretch gap-2 sm:left-auto sm:right-4 sm:max-w-sm">
      {toasts.map((toast) => (
        <ToastRow key={toast.id} toast={toast} />
      ))}
    </div>
  );
}
